import React, { useMemo, useState } from 'react';
import { Meter, StepIcon, clock } from './RunProgress';
import { useElapsed } from '../../hooks/useRunStream';
import { ENRICH_STEPS, enrichStepLabel } from '../../lib/steps';

// The live/paused readout of an enrichment JOB, in the same visual language as the
// import RunProgress (status icons + word, elapsed clock, meters, Show details) but
// driven by the DURABLE run_step counts (GET /runs/{id}/job) so it survives a reload
// and shows a paused job's resume controls. Every USER-FACING step the job will run is
// shown (the internal 'global' registry step is filtered out — see ENRICH_STEPS).
const TEAL = '#00C6AE';

// Order + labels come from the single shared source so this panel and the confirm
// dialog can never drift. 'global' is intentionally not in ENRICH_STEPS, so the merge
// below never surfaces it even if the durable run recorded a global run_step.
const RECIPE_ORDER = ENRICH_STEPS.map((s) => s.name);

const fmt = (n) => Number(n || 0).toLocaleString('en-US');
const labelFor = enrichStepLabel;

// stepPhase collapses a step into ONE consistent state so a fully-processed step
// always reads the same ('done'), whether the backend marked it 'success' or just
// reached its full count (rows_out >= rows_in).
function stepPhase(s) {
  if (s.status === 'failed') return 'failed';
  if (s.status === 'success') return 'done';
  if (s.rows_in && s.rows_in > 0 && (s.rows_out || 0) >= s.rows_in) return 'done';
  if (s.status === 'running' || (s.rows_out || 0) > 0) return 'active';
  return 'waiting';
}

// iconStatus maps a step phase to the RunProgress StepIcon's status vocabulary.
const iconStatus = (phase) =>
  phase === 'done' ? 'success' : phase === 'failed' ? 'failed' : phase === 'active' ? 'running' : 'pending';

function stepFraction(s) {
  const phase = stepPhase(s);
  if (phase === 'done') return 1;
  if (phase === 'active' && s.rows_in > 0) return Math.min(1, (s.rows_out || 0) / s.rows_in);
  return 0;
}

// stepCount is the single clear right-hand label per step.
function stepCount(s) {
  const phase = stepPhase(s);
  if (phase === 'done') return 'Done';
  if (phase === 'failed') return 'Failed';
  if (phase === 'waiting') return 'Waiting';
  const out = s.rows_out || 0; // active
  if (out === 0) return 'Starting…';
  return s.rows_in ? `${fmt(out)} / ${fmt(s.rows_in)}` : `${fmt(out)} done`;
}

function StepRow({ step, isCurrent, live }) {
  const phase = stepPhase(step);
  return (
    <li className="flex items-start gap-3 py-2" aria-label={`step ${step.step_name}`}>
      <StepIcon status={iconStatus(phase)} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <span
            className={`text-sm ${
              phase === 'waiting' ? 'text-gray-400' : isCurrent ? 'font-semibold text-gray-900' : 'text-gray-900'
            }`}
          >
            {labelFor(step.step_name)}
          </span>
          <span
            className={`text-xs tabular-nums flex-none ${
              phase === 'done' ? 'text-emerald-600' : phase === 'failed' ? 'text-red-600 font-medium' : 'text-gray-500'
            }`}
          >
            {stepCount(step)}
          </span>
        </div>
        {phase === 'active' && step.rows_in > 0 && (
          <div className="mt-1.5">
            <Meter value={step.rows_out || 0} max={step.rows_in} height={4} label={labelFor(step.step_name)} live={live} />
          </div>
        )}
      </div>
    </li>
  );
}

export default function EnrichmentJobControls({ job, onPause, onResume, onCancel, pausing }) {
  const [showDetails, setShowDetails] = useState(false);
  const status = job?.status;
  const running = status === 'running';
  const paused = status === 'paused';

  // The FULL ordered step list this job runs (selected subset, else all 7), merging
  // in the run_step rows that exist; steps not started yet are pending. `steps` may
  // arrive as null (a just-started job) — coerce it.
  const actual = useMemo(() => (Array.isArray(job?.steps) ? job.steps : []), [job]);
  const steps = useMemo(() => {
    const byName = {};
    actual.forEach((s) => {
      byName[s.step_name] = s;
    });
    const chosen = Array.isArray(job?.selected) && job.selected.length ? job.selected : RECIPE_ORDER;
    return RECIPE_ORDER.filter((n) => chosen.includes(n)).map(
      (n) => byName[n] || { step_name: n, status: 'pending', rows_in: null, rows_out: null }
    );
  }, [actual, job]);

  // Elapsed clock: tick from the earliest step's start while running.
  const startedAt = useMemo(() => {
    const ts = actual
      .map((s) => s.started_at)
      .filter(Boolean)
      .sort();
    return ts[0] ? new Date(ts[0]).getTime() : null;
  }, [actual]);
  const elapsed = useElapsed(startedAt, running);

  if (!job || (!running && !paused)) return null;

  const overall = steps.length ? Math.round((100 * steps.reduce((a, s) => a + stepFraction(s), 0)) / steps.length) : 0;
  const doneCount = steps.filter((s) => stepPhase(s) === 'done').length;
  // The step being worked / the resume point: prefer the backend's current_step when
  // still in progress, but never a finished step (which looked "current" before).
  const firstUnfinished = steps.find((s) => stepPhase(s) !== 'done');
  const currentEntry = steps.find((s) => s.step_name === job.current_step);
  const current = paused && currentEntry && stepPhase(currentEntry) !== 'done' ? currentEntry : firstUnfinished;

  const subtitle = paused
    ? current
      ? `Resumes at ${labelFor(current.step_name)}`
      : 'Ready to resume'
    : current
    ? `Step ${doneCount + 1} of ${steps.length} · ${labelFor(current.step_name)}`
    : 'Working through the pipeline';

  return (
    <section
      className="bg-white rounded-xl shadow-sm border border-gray-100 p-5"
      aria-label="enrichment job"
      aria-live="polite"
      aria-busy={running}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-gray-900">{paused ? 'Enrichment paused' : 'Enriching your data'}</h2>
          <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
        </div>
        <div className="flex items-center gap-3 flex-none">
          {running && (
            <span className="text-sm tabular-nums text-gray-500" aria-label="Elapsed time">
              {clock(elapsed)}
            </span>
          )}
          {running && (
            <button
              type="button"
              onClick={onPause}
              disabled={pausing}
              className="px-3 py-1.5 rounded-lg text-xs font-medium text-gray-700 border border-gray-300 hover:bg-gray-50 disabled:opacity-60"
            >
              {pausing ? 'Pausing…' : 'Pause'}
            </button>
          )}
          {paused && (
            <button
              type="button"
              onClick={onResume}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
              style={{ background: TEAL, '--tw-ring-color': TEAL }}
            >
              Resume
            </button>
          )}
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 rounded-lg text-xs font-medium text-red-600 border border-red-200 hover:bg-red-50"
          >
            Cancel
          </button>
        </div>
      </div>

      {pausing && running && (
        <p className="mt-3 text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
          Pausing… finishing the current batch, then it stops.
        </p>
      )}

      <div className="mt-4">
        <Meter value={overall} max={100} label="Overall enrichment progress" />
        <p className="text-xs text-gray-400 mt-1.5 tabular-nums">{overall}% overall</p>
      </div>

      <ul className="mt-3 divide-y divide-gray-50">
        {steps.map((s) => (
          <StepRow
            key={s.step_name}
            step={s}
            isCurrent={current && s.step_name === current.step_name}
            live={running}
          />
        ))}
      </ul>

      <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
        <p className="text-sm text-gray-600 tabular-nums">
          <span className="font-semibold text-gray-900">
            {doneCount} of {steps.length}
          </span>{' '}
          steps done
        </p>
        <button
          type="button"
          onClick={() => setShowDetails((v) => !v)}
          className="text-xs text-gray-500 hover:text-gray-900"
          aria-expanded={showDetails}
        >
          {showDetails ? 'Hide details' : 'Show details'}
        </button>
      </div>

      {showDetails && (
        <pre className="mt-3 bg-gray-900 text-gray-100 rounded-lg p-3 max-h-56 overflow-y-auto font-mono text-[11px] leading-relaxed">
          {steps
            .map(
              (s) =>
                `${labelFor(s.step_name).padEnd(18)} ${stepPhase(s).padEnd(8)} ` +
                `${s.rows_in ? `${s.rows_out || 0}/${s.rows_in}` : `${s.rows_out || 0}`}`
            )
            .join('\n')}
        </pre>
      )}
    </section>
  );
}
